import React, { useEffect, useState } from "react";
import Main from "../Main";
import { exportTable } from "../../utils/downloadExcel";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import { useNavigate } from "react-router-dom";
import { autoDateFormat } from "../../utils/autoDateFormate";
import { formatAmount } from "../../utils/formatAmount";
import { fetchDMtTransactionsLists } from "../../api/remitterApi";
import TransactionStatusCheckModel from "./TransactionStatusCheckModel";
import { useNotify } from "../../context/NotifyContext";
import { useTxnDocActions } from "../../hooks/useTxnDocActions";
import {
  ListingFilters,
  ListingPagination,
  SortableTh,
  CopyText,
  AppBreadcrumbs,
  RowActions,
  StatusBadge,
  useListingState,
  TXN_STATUS_OPTIONS,
} from "../../components/listing";

const MoneyList = () => {
  const navigate = useNavigate();
  const { notify } = useNotify();
  const [tableData, setTableData] = useState([]);
  const listing = useListingState({
    sortBy: "date",
    sortDir: "desc",
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalTxnChares, setTotalTxnChares] = useState(0);
  const [totalTxnAmount, setTotalTxnAmount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [selectedTxn, setSelectedTxn] = useState(null);
  const { viewTxn, printTxn, pdfTxn } = useTxnDocActions("dmt");

  const serviceSlug = "dmt";

  const fetch_settalmentsList = async () => {
    try {
      const result = await fetchDMtTransactionsLists(listing.query);
      if (result?.success) {
        setTableData(result.data.withdrawDmt || []);
        setTotalPages(result.data.pagination?.totalPages || 1);
        setTotalCount(result.data.pagination?.total || 0);
        setTotalTxnAmount(result.data?.overAllTrasaction || 0);
        setTotalTxnChares(result.data?.overTxnCharges || 0);
      } else {
        notify(result.message || "Server Error", "error");
      }
    } catch (error) {
      notify("Something went wrong", "error");
    }
  };

  useEffect(() => {
    fetch_settalmentsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    listing.page,
    listing.limit,
    listing.startDate,
    listing.endDate,
    listing.debouncedSearch,
    listing.sortBy,
    listing.sortDir,
    listing.status,
  ]);

  const handleClose = () => {
    setOpenModal(false);
    fetch_settalmentsList();
  };

  const handleView = (txn) => {
    setSelectedTxn(txn);
    setOpenModal(true);
  };

  return (
    <Main showRightPanel={false}>
      <div className="mt-4">
        <AppBreadcrumbs
          items={[
            { label: "Dashboard", to: "/" },
            { label: "Money Transfer" },
            { label: "Transfer History" },
          ]}
        />

        <div className="ds-card mb-3 mt-2 flex items-center justify-between gap-3">
          <h2 className="font-semibold text-[15px] text-[var(--text-dark)]">Transfer History</h2>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => navigate("/dashboard")} className="ds-action-btn">
              <DashboardOutlinedIcon fontSize="small" />
              <span className="hidden md:inline">Dashboard</span>
            </button>
            <button
              type="button"
              className="ds-action-btn"
              onClick={() => navigate("/main-wallet-ledger")}
            >
              <ReplayOutlinedIcon fontSize="small" />
              <span className="hidden md:inline">Wallet History</span>
            </button>
          </div>
        </div>

        <div className="ds-card text-sm">
          <ListingFilters
            onClear={listing.clearFilters}
            startDate={listing.startDate}
            endDate={listing.endDate}
            onStartDate={listing.setStartDate}
            onEndDate={listing.setEndDate}
            search={listing.search}
            onSearch={listing.setSearch}
            searchPlaceholder="Search"
            status={listing.status}
            onStatus={listing.setStatus}
            statusOptions={TXN_STATUS_OPTIONS}
            showApply
            onApply={fetch_settalmentsList}
            applyLabel="Load Records"
            onExport={() =>
              exportTable(
                "dmt-history",
                [
                  { label: "DATE", value: (r) => autoDateFormat(r?.createdAt) },
                  { label: "AMOUNT", key: "amount" },
                  { label: "WITH CHARGE", key: "charges" },
                  { label: "BENEFICIARY NAME", value: (r) => r?.beneficiaryId?.name ?? "" },
                  { label: "A/C NUMBER", key: "accountNumber" },
                  { label: "IFSC CODE", key: "ifsc" },
                  { label: "BANK NAME", value: (r) => r?.beneficiaryId?.bankName ?? "" },
                  { label: "UTR NUMBER", key: "utr" },
                  {
                    label: "MERCHANT CODE-NAME",
                    value: (r) => `${r?.merchantCode || ""}-${r?.merchantName || ""}`,
                  },
                  { label: "MODE", key: "mode" },
                  { label: "STATUS", key: "transactionStatus" },
                ],
                tableData,
                notify
              )
            }
          />

          <div className="ds-table-wrap ds-scroll">
            <table className="ds-table ds-table-listing min-w-[900px]">
              <thead>
                <tr>
                  <SortableTh
                    label="DATE"
                    sortKey="date"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                  <SortableTh
                    label="AMOUNT"
                    sortKey="amount"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                  <th>WITH CHARGE</th>
                  <th>BENEFICIARY NAME</th>
                  <th>A/C NUMBER</th>
                  <th>IFSC CODE</th>
                  <th>BANK NAME</th>
                  <th>UTR NUMBER</th>
                  <th>MERCHANT CODE-NAME</th>
                  <th>MODE</th>
                  <SortableTh
                    label="STATUS"
                    sortKey="status"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                  <th className="ds-th-actions">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {tableData.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="text-center text-[var(--text-muted)]">
                      NO DATA AVAILABLE IN TABLE
                    </td>
                  </tr>
                ) : (
                  tableData.map((row, index) => (
                    <tr key={row?._id || index}>
                      <td>{autoDateFormat(row?.createdAt)}</td>
                      <td className="ds-td-amount">{formatAmount(row?.amount)}</td>
                      <td>{formatAmount(row?.charges)}</td>
                      <td>{row.beneficiaryId?.name}</td>
                      <td>
                        <CopyText value={row.accountNumber} label="Account Number" />
                      </td>
                      <td>{row.ifsc}</td>
                      <td>{row?.beneficiaryId?.bankName}</td>
                      <td>
                        <CopyText value={row?.utr} label="UTR" />
                      </td>
                      <td>{`${row?.merchantCode}-${row?.merchantName}`}</td>
                      <td>{row.mode}</td>
                      <td>
                        <StatusBadge status={row.transactionStatus} />
                      </td>
                      <td>
                        <div className="flex items-center justify-center gap-2">
                          <RowActions
                            onView={() => viewTxn(row)}
                            onPdf={() => pdfTxn(row)}
                            onPrint={() => printTxn(row)}
                          />
                          <button
                            type="button"
                            onClick={() => handleView(row)}
                            className="make-text-primary text-[11px] underline whitespace-nowrap"
                            title="Check status"
                          >
                            Status
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr className="ds-table-total">
                  <td className="ds-table-total__label">TOTAL TRANSFER</td>
                  <td className="ds-table-total__value">₹ {formatAmount(totalTxnAmount)}</td>
                  <td className="ds-td-amount">₹ {formatAmount(totalTxnChares)}</td>
                  <td colSpan="9"></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <ListingPagination
            page={listing.page}
            limit={listing.limit}
            total={totalCount || tableData.length}
            totalPages={totalPages}
            onPageChange={listing.setPage}
            onLimit={listing.setLimit}
          />
        </div>
      </div>

      <TransactionStatusCheckModel
        open={openModal}
        onClose={handleClose}
        txn={selectedTxn}
        slug={serviceSlug}
      />
    </Main>
  );
};

export default MoneyList;
